import { QuestionMarkCircledIcon } from '@radix-ui/react-icons';
import { Box, Flex, Heading, IconButton } from '@radix-ui/themes';
import { CommonBackArrow } from 'components/common/buttons/back-arrow';
import { SuperAdminIcon } from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTooltip } from 'components/common/tooltip';
import { TrainingPerfControls } from 'components/sections/admin-portal/machines/dialogs/training-performance/controls';
import { TrainingPerfIterationsTable } from 'components/sections/admin-portal/machines/dialogs/training-performance/iterations-table';
import { TrainingPerfLogDetails } from 'components/sections/admin-portal/machines/dialogs/training-performance/log-details';
import { TrainingPerfLogsTable } from 'components/sections/admin-portal/machines/dialogs/training-performance/logs-table';
import { TrainingPerfMetrics } from 'components/sections/admin-portal/machines/dialogs/training-performance/metrics';
import { TrainingPerfShotsTable } from 'components/sections/admin-portal/machines/dialogs/training-performance/shots-table';
import useTrainingPerformanceStore from 'components/sections/admin-portal/machines/dialogs/training-performance/store/use-training-performance-store';
import { t } from 'i18next';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IAdminMachine } from 'lib_ts/interfaces/i-machine';
import { IItrLogExt } from 'lib_ts/interfaces/training/i-training-log';
import { useMemo } from 'react';
import { v4 } from 'uuid';

interface IProps {
  machine: IAdminMachine;
}

enum DrillMode {
  None = 'none',
  Iterations = 'iterations',
  Shots = 'shots',
}

export const TrainingPerformance = (props: IProps) => {
  const selectedLog = useTrainingPerformanceStore((s) => s.selectedLog);
  const selectedIteration = useTrainingPerformanceStore(
    (s) => s.selectedIteration
  );

  // for mixin of the rgb color and _id for table selection
  const iterations = useMemo(() => {
    if (!selectedLog) {
      return [];
    }

    return selectedLog.iterations.map((m) => {
      const o: IItrLogExt = {
        ...m,
        rgb: MiscHelper.getRGB(m.fireIDs?.join('-') ?? ''),
        _id: v4(),
      };

      return o;
    });
  }, [selectedLog]);

  const mode = useMemo(() => {
    if (selectedIteration) {
      return DrillMode.Shots;
    }

    if (selectedLog) {
      return DrillMode.Iterations;
    }

    return DrillMode.None;
  }, [selectedLog, selectedIteration]);

  const isDrilling = mode !== DrillMode.None;

  // viewing iterations from a specific log
  const isDrillingIterations = mode === DrillMode.Iterations;

  // viewing shots from a specific iterations
  const isDrillingShots = mode === DrillMode.Shots;

  return (
    <ErrorBoundary identifier="TrainingPerformance">
      <Flex direction="column" gap="4" className={RADIX.VFLEX.GROW}>
        <Flex align="center" gap={RADIX.FLEX.GAP.SM}>
          {mode === DrillMode.Iterations && (
            <CommonBackArrow
              onClick={() => {
                useTrainingPerformanceStore.getState().setSelectedLog(null);
              }}
            />
          )}

          {mode === DrillMode.Shots && (
            <CommonBackArrow
              onClick={() => {
                useTrainingPerformanceStore
                  .getState()
                  .setSelectedIteration(null);
              }}
            />
          )}

          {selectedLog ? (
            <Heading size="4">
              Precision Train Attempts: {selectedLog.pitchName}
            </Heading>
          ) : (
            <Heading size="4">{t('admin.training-performance')}</Heading>
          )}

          <CommonTooltip
            text="Click to get more information about Training Performance"
            trigger={
              <IconButton
                size="1"
                variant="ghost"
                color={RADIX.COLOR.NEUTRAL}
                onClick={() =>
                  window.open(
                    'https://app.intercom.com/a/apps/tas54ol3/knowledge-hub/folder/1910829?activeContentId=11082879&activeContentType=internal-article&editorMode=view&native_content=true',
                    '_blank'
                  )
                }
              >
                <QuestionMarkCircledIcon />
              </IconButton>
            }
          />
          <SuperAdminIcon />
        </Flex>

        <Flex gap="5" direction="column" className={RADIX.VFLEX.GROW}>
          <Box className={isDrilling ? 'hidden' : undefined}>
            <TrainingPerfControls machine={props.machine} />
          </Box>

          <Box className={isDrilling ? 'hidden' : undefined}>
            <TrainingPerfMetrics />
          </Box>

          {/* todo: this is actually busted if the window is too short */}
          <Flex
            direction="column"
            className={StringHelper.classNames([
              isDrilling ? 'hidden' : undefined,
              RADIX.VFLEX.GROW,
            ])}
          >
            <TrainingPerfLogsTable />
          </Flex>

          <Flex
            direction="column"
            gap="4"
            className={!isDrilling ? 'hidden' : undefined}
          >
            <TrainingPerfLogDetails />

            <Box className={isDrillingIterations ? undefined : 'hidden'}>
              <TrainingPerfIterationsTable iterations={iterations} />
            </Box>

            <Box className={isDrillingShots ? undefined : 'hidden'}>
              <TrainingPerfShotsTable />
            </Box>
          </Flex>
        </Flex>
      </Flex>
    </ErrorBoundary>
  );
};
